// CAT-1A (doc 20 §8.3, doc 07 §12.1) -- the client-generated request id that
// makes `createSession` idempotent on the Borna side: minted ONCE per setup
// submission, sent in the request body, and reused when the same submission
// is retried after a failure. Synapse has no correlation id of its own, so
// this is the only thing that lets a retry find the conversation it already
// started instead of creating a second one.
//
// A UUID v4. `crypto.randomUUID` where the browser has it; otherwise the
// same shape from `getRandomValues`; the Math.random fallback exists only
// for environments with no Web Crypto at all (never a modern browser).

function fromRandomValues(cryptoApi) {
  const bytes = new Uint8Array(16)
  cryptoApi.getRandomValues(bytes)
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0'))
  return [
    hex.slice(0, 4).join(''),
    hex.slice(4, 6).join(''),
    hex.slice(6, 8).join(''),
    hex.slice(8, 10).join(''),
    hex.slice(10, 16).join(''),
  ].join('-')
}

function fromMathRandom() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, char => {
    const random = (Math.random() * 16) | 0
    const value = char === 'x' ? random : (random & 0x3) | 0x8
    return value.toString(16)
  })
}

function browserCrypto() {
  return typeof window !== 'undefined' && window.crypto
    ? window.crypto
    : undefined
}

/**
 * @param {{randomUUID?: () => string, getRandomValues?: (bytes: Uint8Array) => Uint8Array}} [cryptoApi]
 *   the Web Crypto object to use; defaults to the browser's (tests pass a fake)
 * @returns {string} a lower-case UUID v4
 */
export function mintRequestId(cryptoApi = browserCrypto()) {
  if (cryptoApi && typeof cryptoApi.randomUUID === 'function') {
    return cryptoApi.randomUUID().toLowerCase()
  }
  if (cryptoApi && typeof cryptoApi.getRandomValues === 'function') {
    return fromRandomValues(cryptoApi)
  }
  return fromMathRandom()
}
